import { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useGlobalSectionStyles } from "../../../../../src/hooks/panelEditor/useGlobalSectionStyles";
import StyleAttributeForm from "./components/StyleAttributeForm";
import PlecaStyleForm from "./components/PlecaStyleForm";
import ColorPickerModal from "../ColorPickerModal.tsx";
import { sanitizeStyleValue } from "./utils/styleValidation";
import { STYLE_ATTRIBUTES, AVAILABLE_FONTS, AVAILABLE_POSITIONS, DEFAULT_STYLE_VALUES } from "./constants/styleDefaults";

const GlobalStylesEditor = ({ token, documentId, onSwitchTab, onNavigateToWitnessAlignment }) => {
  const {
    sectionStyles,
    loading,
    saving,
    error: apiError,
    updateGlobalStyles
  } = useGlobalSectionStyles(token, documentId);

  const [localStyles, setLocalStyles] = useState({});
  const [feedback, setFeedback] = useState({ open: false, message: "", severity: "success" });
  
  const [colorPicker, setColorPicker] = useState({
    open: false,
    attributeId: null,
    color: "#000000"
  });

  const allSectionsNull = sectionStyles && sectionStyles.length > 0 && sectionStyles.every(section => section.type === null);
  const noSectionsAvailable = sectionStyles && sectionStyles.length === 0;

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (
      !initialized &&
      !loading &&
      sectionStyles &&
      sectionStyles.length > 0 &&
      sectionStyles.some(s => s.styles)
    ) {
      const defaults = {};

      STYLE_ATTRIBUTES.forEach(attr => {
        let backendStyle = {};

        const sectionWithAttr = sectionStyles.find(
          s => s.styles && s.styles[attr.id]
        );

        if (sectionWithAttr) {
          backendStyle = sectionWithAttr.styles[attr.id];
        }

        defaults[attr.id] = {
          ...DEFAULT_STYLE_VALUES,
          ...backendStyle,
          fontSize: backendStyle.fontSize
            ? Number(backendStyle.fontSize)
            : DEFAULT_STYLE_VALUES.fontSize,
          show:
            backendStyle.show !== undefined
              ? backendStyle.show
              : DEFAULT_STYLE_VALUES.show,
        };
      });

      setLocalStyles(defaults);
      setInitialized(true);
    }
  }, [sectionStyles, loading, initialized]);

  const handleAttributeChange = (attributeId, newValues) => {
    setLocalStyles(prev => ({
      ...prev,
      [attributeId]: newValues
    }));
  };

  const handleSave = async () => {
    const sanitizedStyles = {};
    Object.keys(localStyles).forEach(key => {
      sanitizedStyles[key] = sanitizeStyleValue(key, localStyles[key]);
    });

    const result = await updateGlobalStyles(sanitizedStyles);
    if (result.success) {
      setFeedback({
        open: true,
        message: "Estilos globales aplicados correctamente a todas las secciones",
        severity: "success"
      });
    } else {
      setFeedback({
        open: true,
        message: result.error || "Error al guardar los estilos",
        severity: "error"
      });
    }
  };

  const handleOpenColorPicker = (attributeId, currentColor) => {
    setColorPicker({
      open: true,
      attributeId,
      color: currentColor || "#000000"
    });
  };

  const handleColorApply = (newColor) => {
    if (colorPicker.attributeId) {
      const currentAttrStyles = localStyles[colorPicker.attributeId] || DEFAULT_STYLE_VALUES;
      handleAttributeChange(colorPicker.attributeId, {
        ...currentAttrStyles,
        color: newColor
      });
    }
    setColorPicker(prev => ({ ...prev, open: false }));
  };

  const handleCloseFeedback = () => setFeedback(prev => ({ ...prev, open: false }));

  if (loading && Object.keys(localStyles).length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box position="relative">
      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}

      {(allSectionsNull || noSectionsAvailable) && !loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            backdropFilter: "blur(3px)",
            backgroundColor: "rgba(255, 255, 255, 0.10)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            color="primary"
            sx={{ mb: 2, fontWeight: 600 }}
          >
            {noSectionsAvailable
              ? "No hay secciones en este documento."
              : "No hay secciones con un tipo asignado."}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {noSectionsAvailable
              ? "Agrega secciones en el editor principal primero."
              : "Edita los tipos de sección para habilitar la edición de estilos globales."}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onSwitchTab && onSwitchTab(0)}
            sx={{
              borderRadius: "50px",
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Ir a Edición por secciones
          </Button>
        </Box>
      )}

      <Box
        mb={3}
        sx={{
          opacity: allSectionsNull || noSectionsAvailable ? 1 : 1,
          pointerEvents:
            allSectionsNull || noSectionsAvailable ? "none" : "auto",
        }}
      >
        {STYLE_ATTRIBUTES.map((attr) => (
          <Accordion key={attr.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="500">{attr.label}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {attr.id === "pleca" ? (
                <PlecaStyleForm
                  attributeId={attr.id}
                  label={attr.label}
                  values={localStyles[attr.id] || DEFAULT_STYLE_VALUES}
                  onChange={handleAttributeChange}
                  onOpenColorPicker={handleOpenColorPicker}
                />
              ) : (
                <StyleAttributeForm
                  attributeId={attr.id}
                  label={attr.label}
                  values={localStyles[attr.id] || DEFAULT_STYLE_VALUES}
                  onChange={handleAttributeChange}
                  onOpenColorPicker={handleOpenColorPicker}
                  fonts={AVAILABLE_FONTS}
                  positions={AVAILABLE_POSITIONS}
                  showPosition={attr.id !== "witnesses"}
                />
              )}
              {attr.id === "content" && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ ml: 2, fontStyle: "italic", display: "block" }}
                >
                  ⓘ La alineacion aplicada tambien se vera reflejada en los
                  testigos, solo sí los testigos estan concatenados.
                </Typography>
              )}
              {attr.id === "witnesses" && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onNavigateToWitnessAlignment}
                  sx={{ textTransform: "none", ml: 2, mt: 2 }}
                >
                  Configurar alineación de testigos
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || allSectionsNull || noSectionsAvailable}
          sx={{
            bgcolor: "#1976d2",
            color: "white",
            borderRadius: "50px",
            px: 4,
            py: 1.5,
            textTransform: "capitalize",
            "&:hover": { bgcolor: "#1565c0" },
          }}
        >
          {saving ? "Aplicando..." : "Aplicar a todas las secciones"}
        </Button>
      </Box>

      <ColorPickerModal
        open={colorPicker.open}
        title="Seleccionar color"
        color={colorPicker.color}
        onClose={() => setColorPicker((prev) => ({ ...prev, open: false }))}
        onApply={handleColorApply}
      />

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={feedback.severity}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GlobalStylesEditor;
